import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import style from "../../Admin/admin.module.css";
import { Button, LabelBox } from "../../../components";
import { colors } from "../../../theme/colors";
import { CustomTable, StatusBadge } from "../../../components/DataTable";
import { CustomActionMenu } from "../../../components/Menu";
import {
  useGetUsersListQuery,
  useGetUserRolesQuery,
  useUpdateUserMutation,
} from "../../../services/api";
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../../components/Dialog";
import { Input } from "../../../components/UserInput";
import { updateStatusSchema } from "../../../yup";
import { getErrorMessage } from "../../../utils/common";
import {
  buildUserActions,
  renderCellText,
  resetDeactivateReasonField,
  useStatusDialogs,
} from "../userShared";
import { DEFAULT_NURSE_ROLE_ID } from "../../../utils/constants";

/** Form values inferred from the update status schema. */
type FormState = typeof updateStatusSchema.__outputType;
/** Action identifiers for the nurse action menu. */
type NurseAction = "view" | "edit" | "logs" | "deactivate" | "activate";

/** Props for the nurse list tab. */
type NurseTabProps = {
  searchKey?: string;
  onClearSearch?: () => void;
};

/**
 * Formats a nurse name for list rendering.
 *
 * Args:
 *   item: User row data from the API.
 *
 * Returns:
 *   JSX.Element: Rendered name text.
 */
const renderNameCell = (item: Users) =>
  renderCellText(
    `${item.surname?.toUpperCase() ?? ""}, ${item.given_name ?? ""} ${
      item.prefix ?? ""
    }`,
    { className: style.cell }
  );

/**
 * Renders a status badge for list rows.
 *
 * Args:
 *   isActive: Status flag from the API.
 *
 * Returns:
 *   JSX.Element: Status badge component.
 */
const renderStatusBadge = (isActive?: boolean) => (
  <StatusBadge isActive={isActive ?? false} />
);

/**
 * Renders the nurse list tab with search and status actions.
 *
 * Args:
 *   searchKey: Optional search query.
 *
 * Returns:
 *   JSX.Element: Nurse list tab content.
 */
const NurseTab = ({ searchKey, onClearSearch }: NurseTabProps) => {
  const { id } = useParams();
  const healthOrgId = Number.isFinite(Number(id)) ? Number(id) : undefined;
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [selectedNurse, setSelectedNurse] = useState<Users | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    confirmOpen,
    reasonOpen,
    logsOpen,
    openConfirm,
    closeConfirm,
    openReason,
    closeReason,
    openLogs,
    closeLogs,
  } = useStatusDialogs();

  const { data: rolesData } = useGetUserRolesQuery({
    is_active: true,
    is_detailed: true,
  });

  const nurseRoleId = useMemo(
    () =>
      rolesData?.items?.find((role: { name: string }) => role?.name === "Nurse")
        ?.id,
    [rolesData]
  );

  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState({
    search_key: searchKey,
    order_by_columns: "surname",
    order: "Asc",
    is_detailed: true,
    health_org_id: healthOrgId,
    user_role_id: nurseRoleId ?? DEFAULT_NURSE_ROLE_ID,
  });

  const {
    data: nursesData,
    refetch: refetchUsers,
    isFetching,
  } = useGetUsersListQuery(queryParam);

  useEffect(() => {
    if (nurseRoleId) {
      setQueryParam((prevParams) => ({
        ...prevParams,
        user_role_id: nurseRoleId,
      }));
    }
  }, [nurseRoleId]);

  useEffect(() => {
    setQueryParam((prevParams) => ({
      ...prevParams,
      search_key: searchKey === "" ? undefined : searchKey,
      offset: 0,
    }));
  }, [searchKey]);

  const handleMenu = (value: NurseAction, nurse: Users) => {
    setSelectedNurse(nurse);
    switch (value) {
      case "deactivate":
      case "activate":
        openConfirm();
        break;
      case "view":
        navigate(`/users/details/${nurse?.id}`, {
          state: {
            userRole: nurseRoleId,
            userType: "NURSE",
            source: "USER_DETAILS",
            healthOrgId,
          },
        });
        break;
      case "edit":
        navigate(`/users/edit/${nurse?.id}`, {
          state: { userType: "NURSE" },
        });
        break;
      case "logs":
        openLogs();
        break;
      default:
        break;
    }
  };

  const fields = [
    {
      id: "surname",
      label: "Name",
      flex: 2.5,
      sortable: true,
      type: "name",
      render: (item: Users) => renderNameCell(item),
    },
    {
      id: "mail",
      label: "Email",
      flex: 2.5,
      render: (item: Users) =>
        renderCellText(`${item.mail ?? ""}`, { className: style.cell }),
    },
    {
      id: "is_active",
      label: "Status",
      type: "badge",
      flex: 1.3,
      sortable: true,
      render: (item: Users) => renderStatusBadge(item.is_active),
    },
    {
      id: "logo_s3_key_name",
      label: "Actions",
      type: "action",
      render: (item: Users) => (
        <CustomActionMenu<Users, NurseAction>
          item={item}
          isActive={item?.is_active ?? false}
          handleChange={handleMenu}
          actions={buildUserActions<NurseAction>([
            "view",
            ...(item.is_active ? (["edit"] as NurseAction[]) : []),
            "logs",
            item.is_active ? "deactivate" : "activate",
          ])}
        />
      ),
    },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  /**
   * Updates nurse activation status with an optional deactivation reason.
   *
   * Args:
   *   values: Validated form payload.
   *
   * Returns:
   *   Promise<void>: Resolves after status update and refetch.
   */
  const onSubmit = async (values: FormState) => {
    if (!selectedNurse?.id) {
      setMessage({
        type: "error",
        text: "No nurse selected for status update.",
      });
      return;
    }
    try {
      setIsLoading(true);
      const { deactivate_reason } = values;
      await updateUser({
        id: selectedNurse.id,
        is_active: !selectedNurse.is_active,
        deactivate_reason: deactivate_reason ?? "",
      }).unwrap();
      // Explicit toast and refetch keep UI feedback and table state in sync after status changes.
      setMessage({
        type: "success",
        text: "Status updated successfully.",
      });
      closeReason();
      resetDeactivateReasonField(setValue, clearErrors);
      await refetchUsers();
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Render the Nurse component
  return (
    <>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box
        style={{
          maxHeight: "calc(100vh - 195px)",
          overflow: "auto",
          marginTop: "10px",
        }}
      >
        <CustomTable<Users>
          fields={fields}
          paginationRequired={true}
          data={nursesData?.items ?? []}
          loading={isFetching}
          onDoubleClick={(nurse: Users) => {
            navigate(`/users/details/${nurse?.id}`, {
              state: {
                userRole: nurseRoleId,
                userType: "NURSE",
                healthOrgId,
              },
            });
          }}
          defaultOrderBy={"surname"}
          searchTerm={searchKey}
          onClearSearch={onClearSearch}
        />
      </Box>
      {confirmOpen && (
        // Deactivation requires a reason; activation proceeds immediately.
        <ConfirmationDialog
          visible={confirmOpen}
          message={`Are you sure you want to ${
            selectedNurse?.is_active ? "deactivate " : "activate "
          }"${selectedNurse?.surname?.toUpperCase() ?? ""}, ${
            selectedNurse?.given_name ?? ""
          } ${selectedNurse?.prefix ?? ""}"?`}
          onClose={() => {
            closeConfirm();
          }}
          onConfirm={() => {
            closeConfirm();
            if (selectedNurse?.is_active) {
              openReason();
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonOpen && (
        <InputDialog
          visible={reasonOpen}
          onClose={() => {
            closeReason();
            resetDeactivateReasonField(setValue, clearErrors);
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  closeReason();
                  resetDeactivateReasonField(setValue, clearErrors);
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logsOpen && (
        <DetailsDialog
          visible={logsOpen}
          details={selectedNurse}
          onClose={() => {
            closeLogs();
          }}
          onConfirm={() => {
            closeLogs();
          }}
        />
      )}
    </>
  );
};

export default NurseTab;
